import React, { useCallback, useEffect, useRef, useState } from "react";
import { verifyThumbUrl } from "./api";

/** SHEET-NUM-1 — one rendered page, loaded only when it comes into view.
 *
 *  The verification grid used to point at an endpoint that did not exist, so every card showed a
 *  broken image and nobody could check a sheet number against the sheet. Now each card asks for
 *  its own picture as it scrolls into view — a 62-page set never blocks the dialog — and a page
 *  that genuinely will not render says so instead of showing a torn-paper icon.
 */
export function PagePreview({ uploadId, part, page, width = 420, className = "", onExpand, label }) {
  const [state, setState] = useState("idle");        // idle | loading | ok | failed
  const box = useRef(null);

  useEffect(() => {
    const el = box.current;
    if (!el || state !== "idle") return undefined;
    if (typeof IntersectionObserver === "undefined") { setState("loading"); return undefined; }
    const io = new IntersectionObserver((rows) => {
      if (rows.some((r) => r.isIntersecting)) { setState("loading"); io.disconnect(); }
    }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [state]);

  return (
    <div ref={box} className={`relative bg-neutral-100 rounded border overflow-hidden ${onExpand ? "cursor-zoom-in" : ""} ${className}`}
         onClick={onExpand} data-testid={`verify-preview-${page}`} data-state={state}>
      {state === "idle" && <div className="absolute inset-0 grid place-items-center text-[11px] text-neutral-400">page {page + 1}</div>}
      {state === "loading" && <div className="absolute inset-0 grid place-items-center text-[11px] text-neutral-400 animate-pulse">drawing page {page + 1}…</div>}
      {state === "failed" && (
        <div className="absolute inset-0 grid place-items-center p-2 text-center text-[11px] text-red-700" data-testid={`verify-preview-failed-${page}`}>
          This page could not be drawn.<br />Type the sheet number by hand.
        </div>
      )}
      {state !== "idle" && state !== "failed" && (
        <img src={verifyThumbUrl(uploadId, part, page, width)} alt={label || `page ${page + 1}`} loading="lazy" decoding="async"
             onLoad={() => setState("ok")} onError={() => setState("failed")}
             className={`w-full h-full object-contain transition-opacity ${state === "ok" ? "opacity-100" : "opacity-0"}`}
             data-testid={`verify-thumb-${page}`} />
      )}
      {state === "ok" && onExpand && (
        <span className="absolute bottom-1 right-1 rounded bg-black/60 text-white text-[10px] px-1.5 py-0.5">tap to enlarge</span>
      )}
    </div>
  );
}

/** React attaches onWheel passively, so `preventDefault` there is ignored and the page scrolls
 *  under the viewer while you zoom. The listener has to be registered by hand as non-passive. */
export function useWheelZoom(ref, step) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const on = (e) => { e.preventDefault(); step(e.deltaY > 0 ? -0.25 : 0.25); };
    el.addEventListener("wheel", on, { passive: false });
    return () => el.removeEventListener("wheel", on);
  }, [ref, step]);
}

/** A sheet at a size you can actually read, with zoom and drag-to-pan. Used by the grid's enlarge
 *  and by "Read by region", which cannot work on a thumbnail. */
export function PageZoom({ uploadId, part, page, title, onClose }) {
  const [z, setZ] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const stage = useRef(null);
  const step = useCallback((d) => setZ((v) => Math.min(6, Math.max(1, Number((v + d).toFixed(2))))), []);
  useWheelZoom(stage, step);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex flex-col" data-testid="page-zoom">
      <div className="p-3 flex items-center gap-2 text-white text-sm">
        <span className="font-semibold truncate">{title || `Page ${page + 1}`}</span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => step(-0.5)} className="w-9 h-9 rounded border border-white/40" data-testid="page-zoom-out">−</button>
          <span className="tabular-nums w-12 text-center" data-testid="page-zoom-level">{Math.round(z * 100)}%</span>
          <button type="button" onClick={() => step(0.5)} className="w-9 h-9 rounded border border-white/40" data-testid="page-zoom-in">+</button>
          <button type="button" onClick={() => { setZ(1); setOff({ x: 0, y: 0 }); }} className="px-2 h-9 rounded border border-white/40" data-testid="page-zoom-reset">Fit</button>
          <button type="button" onClick={onClose} className="px-3 h-9 rounded bg-white text-neutral-900 font-semibold" data-testid="page-zoom-close">Close</button>
        </div>
      </div>
      <div ref={stage} className="flex-1 overflow-hidden grid place-items-center touch-none"
           onPointerDown={(e) => { drag.current = { x: e.clientX - off.x, y: e.clientY - off.y }; }}
           onPointerMove={(e) => { if (drag.current) setOff({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); }}
           onPointerUp={() => { drag.current = null; }}
           style={{ cursor: z > 1 ? "grab" : "default" }}>
        <img src={verifyThumbUrl(uploadId, part, page, 1800)} alt={`page ${page + 1} enlarged`} draggable={false}
             style={{ transform: `translate(${off.x}px, ${off.y}px) scale(${z})`, transformOrigin: "center" }}
             className="max-h-full max-w-full bg-white" data-testid="page-zoom-image" />
      </div>
    </div>
  );
}
